import type { Measure } from '@kanvas/board-core';

/**
 * Text measurement with a bounded cache.
 *
 * Word wrapping calls this once per candidate line, which during a resize drag
 * means thousands of calls per second. `measureText` is not free, so results
 * are memoised on `font|text`. The cache is cleared wholesale when it grows
 * past the cap rather than tracking an LRU: the working set is small and the
 * refill cost after a clear is a few frames of measuring.
 */
const CACHE_LIMIT = 20000;

let ctx: CanvasRenderingContext2D | null = null;
const cache = new Map<string, number>();

/** Bumped whenever cached metrics are thrown away, so layouts built on them are too. */
let generation = 0;

export function measureGeneration(): number {
  return generation;
}

function context(): CanvasRenderingContext2D {
  if (ctx) return ctx;
  const canvas = document.createElement('canvas');
  const c = canvas.getContext('2d');
  if (!c) throw new Error('Canvas 2D is unavailable');
  ctx = c;
  return c;
}

export const measureText: Measure = (text, font) => {
  if (text === '') return 0;
  const key = `${font}|${text}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const c = context();
  if (c.font !== font) c.font = font;
  const width = c.measureText(text).width;

  if (cache.size >= CACHE_LIMIT) cache.clear();
  cache.set(key, width);
  return width;
};

/** Call after web fonts load, since metrics change once the real face arrives. */
export function clearMeasureCache(): void {
  cache.clear();
  generation++;
}

/** Resolves when the fonts the board uses are ready, so text does not reflow. */
export async function waitForFonts(): Promise<void> {
  if (!('fonts' in document)) return;
  try {
    await document.fonts.ready;
  } finally {
    clearMeasureCache();
  }
}
